import { useEffect, useMemo, useState } from "react";

type MetricKey = "visitors" | "optinRate" | "webinarShowRate" | "pitchRetentionRate" | "fullApplicationRate" | "bookingRate" | "callShowRate" | "closeRate" | "cashPerClose";
type Metrics = Record<MetricKey, number>;

interface WebinarOption { id: string; name: string; date: string; windowStart: string; windowEnd: string }
interface AcquisitionData { totalOptins: number; totalVisitors: number; totalOptinRate: number | null; trafficAvailable: boolean; startDate: string; endDate: string }
interface EnhancedData {
  onWebinar: { registrants: number; attended: number; showRate: number; retentionAtPitch: number; attendeesAtPitch: number };
  salesTotal: { fullApps: number; bookedCalls: number; takenCalls: number; closed: number; closeRate: number; showRate: number; cash: number; auv: number };
  totalEocsDue: number;
  totalEocsTaken: number;
}

const DEFAULTS: Metrics = {
  visitors: 5000,
  optinRate: 20,
  webinarShowRate: 35,
  pitchRetentionRate: 70,
  fullApplicationRate: 12,
  bookingRate: 25,
  callShowRate: 70,
  closeRate: 25,
  cashPerClose: 5000,
};

// Sourced from Webinars.tsx SALES_TARGETS + ON_WEBINAR_TARGETS
const AUDIENCE_TARGETS = {
  cold: {
    optinRate:           { min: 20, max: 30 },
    webinarShowRate:     { min: 20, max: 30 },
    pitchRetentionRate:  { min: 70, max: 85 },
    fullApplicationRate: { min: 30, max: 45 },
    bookingRate:         { min: 20, max: 30 },
    callShowRate:        { min: 60, max: 70 },
    closeRate:           { min: 25, max: 35 },
  },
  warm: {
    optinRate:           { min: 40, max: 55 },
    webinarShowRate:     { min: 35, max: 50 },
    pitchRetentionRate:  { min: 80, max: 90 },
    fullApplicationRate: { min: 40, max: 60 },
    bookingRate:         { min: 30, max: 50 },
    callShowRate:        { min: 70, max: 85 },
    closeRate:           { min: 35, max: 50 },
  },
} as const;

type AudienceType = "cold" | "warm";

const numberFormat = new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 });
const moneyFormat = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const count = (value: number) => numberFormat.format(value);
const money = (value: number) => moneyFormat.format(value);

interface TargetRange { min: number; max: number }

type PerfStatus = "above" | "on" | "below" | null;
function getPerfStatus(value: number, target?: TargetRange): PerfStatus {
  if (!target) return null;
  if (value > target.max) return "above";
  if (value < target.min) return "below";
  return "on";
}

function NumericControl({
  label, hint, value, suffix, min = 0, max, step = 1, target, onChange,
}: {
  label: string;
  hint: string;
  value: number;
  suffix: string;
  min?: number;
  max: number;
  step?: number;
  target?: TargetRange;
  onChange: (value: number) => void;
}) {
  const safeValue = Math.min(max, Math.max(min, Number.isFinite(value) ? value : min));
  const displayedValue = suffix === "$" ? money(safeValue) : suffix === "%" ? `${safeValue.toFixed(step < 1 ? 1 : 0)}%` : `${count(safeValue)} ${suffix}`;
  const range = max - min;
  const targetLeftPct = target ? Math.max(0, ((target.min - min) / range) * 100) : 0;
  const targetWidthPct = target ? Math.min(100 - targetLeftPct, ((target.max - target.min) / range) * 100) : 0;
  const perf = getPerfStatus(safeValue, target);

  return (
    <label className={`block rounded-xl border bg-background/40 p-3 transition-colors hover:border-[var(--brand-primary)]/50 ${
      perf === "above" ? "border-green-500/50" : perf === "below" ? "border-red-500/50" : "border-border"
    }`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <span className="block text-sm font-semibold text-foreground">{label}</span>
          <span className="mt-0.5 block text-xs leading-4 text-muted-foreground">{hint}</span>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className="whitespace-nowrap font-mono text-base font-bold tabular-nums text-[var(--brand-primary)]">{displayedValue}</span>
          {perf === "above" && (
            <span className="flex items-center gap-0.5 rounded-full bg-green-500/15 px-2 py-0.5 text-[10px] font-bold text-green-400">
              ↑ Exceeding
            </span>
          )}
          {perf === "below" && (
            <span className="flex items-center gap-0.5 rounded-full bg-red-500/15 px-2 py-0.5 text-[10px] font-bold text-red-400">
              ↓ Below target
            </span>
          )}
          {perf === "on" && (
            <span className="flex items-center gap-0.5 rounded-full bg-green-500/10 px-2 py-0.5 text-[10px] font-bold text-green-500/70">
              ✓ On target
            </span>
          )}
        </div>
      </div>

      <div className="mt-4">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={safeValue}
          onChange={(event) => onChange(Number(event.target.value))}
          aria-label={label}
          className="h-2 w-full cursor-pointer appearance-none rounded-full bg-muted accent-[var(--brand-primary)]"
        />
        {target && (
          <div className="relative mt-1.5 h-1 w-full rounded-full bg-muted/40">
            <div
              className="absolute h-full rounded-full bg-yellow-400"
              style={{ left: `${targetLeftPct}%`, width: `${targetWidthPct}%` }}
            />
          </div>
        )}
      </div>

      <div className="mt-1.5 flex items-center justify-between text-[10px] font-medium tabular-nums text-muted-foreground">
        <span>{suffix === "$" ? money(min) : `${count(min)}${suffix === "visitors" ? "" : suffix}`}</span>
        {target && (
          <span className="font-semibold text-yellow-400">
            Target: {target.min}{suffix === "%" ? "–" + target.max + "%" : "–" + target.max}
          </span>
        )}
        <span>{suffix === "$" ? money(max) : `${count(max)}${suffix === "visitors" ? "" : suffix}`}</span>
      </div>
    </label>
  );
}

function ResultRow({ label, value, rate, rateLabel, final }: { label: string; value: number; rate?: number; rateLabel?: string; final?: boolean }) {
  return (
    <div className={`flex items-center justify-between gap-4 border-b border-border/60 py-3 last:border-b-0 ${final ? "rounded-xl bg-emerald-500/10 px-4 py-4" : ""}`}>
      <div>
        <p className={`text-sm ${final ? "font-bold text-emerald-400" : "font-medium text-foreground"}`}>{label}</p>
        {rate !== undefined && <p className="mt-0.5 text-xs text-muted-foreground">{rate.toFixed(1)}% {rateLabel ?? "conversion from prior stage"}</p>}
      </div>
      <span className={`font-mono text-lg font-bold tabular-nums ${final ? "text-emerald-400" : "text-foreground"}`}>{count(value)}</span>
    </div>
  );
}

export function PreWebinarProjections() {
  const [audienceType, setAudienceType] = useState<AudienceType>(() => {
    const stored = localStorage.getItem("cr8her-projection-audience");
    return stored === "warm" ? "warm" : "cold";
  });
  const [metrics, setMetrics] = useState<Metrics>(() => {
    try { return { ...DEFAULTS, ...JSON.parse(localStorage.getItem("cr8her-webinar-projection-v2") || "{}") }; } catch { return DEFAULTS; }
  });
  const [webinars, setWebinars] = useState<WebinarOption[]>([]);
  const [selectedWebinar, setSelectedWebinar] = useState("");
  const [selectedWebinarIds, setSelectedWebinarIds] = useState<string[]>([]);
  const [loadingActuals, setLoadingActuals] = useState(false);
  const [loadedName, setLoadedName] = useState<string | null>(null);
  const [loadNote, setLoadNote] = useState<string | null>(null);
  // eocFactor = avg(eocsDue / bookedCalls) — the fraction of booked calls that actually
  // come due (i.e. not cancelled by sales or rescheduled). Loaded from actuals so the
  // formula takenCalls = bookedCalls × eocFactor × EOC_callShowRate stays correct while
  // the slider can display the intuitive EOC show rate (takenCalls / eocsDue).
  // Historical average cash is kept separate from the modeled funnel so independently averaged KPIs cannot compound into a fictional historical result.
  const [benchmarkActualCash, setBenchmarkActualCash] = useState<number | null>(null);
  const [benchmarkWebinarCount, setBenchmarkWebinarCount] = useState(0);
  const [eocFactor, setEocFactor] = useState(1.0);

  const targets = AUDIENCE_TARGETS[audienceType];

  const switchAudience = (type: AudienceType) => {
    setAudienceType(type);
    localStorage.setItem("cr8her-projection-audience", type);
  };

  useEffect(() => { localStorage.setItem("cr8her-webinar-projection-v2", JSON.stringify(metrics)); }, [metrics]);
  useEffect(() => {
    fetch("/api/webinar2/list").then((r) => r.json()).then((d) => {
      if (d.success) setWebinars(d.webinars || []);
    }).catch(() => undefined);
  }, []);

  const update = (key: MetricKey, value: number) => {
    setBenchmarkActualCash(null);
    setBenchmarkWebinarCount(0);
    setMetrics((current) => ({ ...current, [key]: value }));
  };

  const projection = useMemo(() => {
    const optins     = metrics.visitors * metrics.optinRate / 100;
    const attended   = optins * metrics.webinarShowRate / 100;
    const atPitch    = attended * metrics.pitchRetentionRate / 100;
    const fullApps   = atPitch * metrics.fullApplicationRate / 100;
    // Booking rate is from app pitch viewers (atPitch), not full applications
    const bookedCalls = atPitch * metrics.bookingRate / 100;
    // callShowRate is the EOC show rate (takenCalls / eocsDue).
    // eocFactor (eocsDue / bookedCalls) is loaded from actuals and bridges
    // the gap between booked calls and calls that actually come due.
    const eocsDue    = bookedCalls * eocFactor;
    const takenCalls  = eocsDue * metrics.callShowRate / 100;
    const closes      = takenCalls * metrics.closeRate / 100;
    const cash        = closes * metrics.cashPerClose;
    return { optins, attended, atPitch, fullApps, bookedCalls, eocsDue, takenCalls, closes, cash, cashPerCall: takenCalls > 0 ? cash / takenCalls : 0 };
  }, [metrics, eocFactor]);

  const loadWebinarActuals = async (overrideId?: string, selectedOverride?: WebinarOption[]) => {
    const id = overrideId ?? selectedWebinar;
    if (!id) return;

    // ── Combined benchmark: all webinars or a selected subset ───────────────
    if (id === "__average__" || id === "__selected__") {
      const webinarsToBenchmark = selectedOverride ?? webinars;
      if (webinarsToBenchmark.length === 0) { setLoadNote("No webinars found to benchmark."); return; }
      setLoadingActuals(true); setLoadNote(null);
      try {
        // Fetch enhanced + acquisition for every webinar in parallel
        const results = await Promise.all(
          webinarsToBenchmark.map(async (w) => {
            try {
              const acquisitionParams = new URLSearchParams({ startDate: w.windowStart, endDate: w.windowEnd });
              const [eRes, aRes] = await Promise.all([
                fetch(`/api/webinar2/enhanced/${encodeURIComponent(w.id)}`),
                fetch(`/api/webinar-acquisition?${acquisitionParams.toString()}`),
              ]);
              const [ePay, aPay] = await Promise.all([eRes.json(), aRes.json()]);
              if (!ePay.success) return null;
              return { enhanced: ePay.data as EnhancedData, acquisition: aPay.success ? aPay.data as AcquisitionData : null };
            } catch { return null; }
          })
        );

        const valid = results.filter((r): r is { enhanced: EnhancedData; acquisition: AcquisitionData | null } => r !== null);
        if (valid.length === 0) throw new Error("No webinar data could be loaded for averaging.");

        const avg = (vals: number[]) => vals.filter((v) => v > 0).reduce((s, v) => s + v, 0) / (vals.filter((v) => v > 0).length || 1);

        // Combined benchmarks are calculated from combined numerators and
        // denominators. Averaging individual percentages gives every webinar equal
        // weight and produces a rate that does not reconcile to the funnel totals.
        const totalRegistrants = valid.reduce((sum, r) => sum + r.enhanced.onWebinar.registrants, 0);
        const totalAttended = valid.reduce((sum, r) => sum + r.enhanced.onWebinar.attended, 0);
        const totalAtPitch = valid.reduce((sum, r) => sum + r.enhanced.onWebinar.attendeesAtPitch, 0);
        const totalFullApps = valid.reduce((sum, r) => sum + r.enhanced.salesTotal.fullApps, 0);
        const totalBookedCalls = valid.reduce((sum, r) => sum + r.enhanced.salesTotal.bookedCalls, 0);
        const dashboardResponse = await fetch("/api/dashboard/summary?funnelType=webinar&dateField=timestamp");
        const dashboardPayload = await dashboardResponse.json() as {
          success: boolean;
          data?: { metrics?: { callsDue: number; callsTaken: number; callsClosed: number; totalCashCollected: number } };
          error?: string;
        };
        const dashboardSales = dashboardPayload.data?.metrics;
        if (!dashboardResponse.ok || !dashboardPayload.success || !dashboardSales) {
          throw new Error(dashboardPayload.error || "Verified webinar sales metrics are unavailable.");
        }

        // Sales-call KPIs must use the same mutually exclusive webinar EOC cohort as
        // Dashboard. The per-webinar enhanced series is retained for webinar-stage
        // analysis but is not a valid blended sales denominator because it does not
        // apply Book-a-Call precedence across the combined population.
        const totalEocsDue = dashboardSales.callsDue;
        const totalEocsTaken = dashboardSales.callsTaken;
        const totalTakenCalls = dashboardSales.callsTaken;
        const totalCash = dashboardSales.totalCashCollected;
        const totalWins = dashboardSales.callsClosed;
        const webinarShowRate = totalRegistrants > 0 ? totalAttended / totalRegistrants * 100 : 0;
        const pitchRetentionRate = totalAttended > 0 ? totalAtPitch / totalAttended * 100 : 0;
        const fullApplicationRate = totalAtPitch > 0 ? totalFullApps / totalAtPitch * 100 : 0;
        const bookingRate = totalAtPitch > 0 ? totalBookedCalls / totalAtPitch * 100 : 0;
        const eocFactor = totalBookedCalls > 0 ? totalEocsDue / totalBookedCalls : 1;
        const callShowRate = totalEocsDue > 0 ? totalEocsTaken / totalEocsDue * 100 : 0;
        const closeRate = totalTakenCalls > 0 ? totalWins / totalTakenCalls * 100 : 0;
        const cashPerClose = totalWins > 0 ? totalCash / totalWins : 0;

        // Acquisition (opt-in rate / visitors) — only where GA data is available
        const acqValid = valid.filter((r) => r.acquisition?.trafficAvailable && r.acquisition.totalOptinRate !== null);
        const totalAcqVisitors = acqValid.reduce((sum, r) => sum + r.acquisition!.totalVisitors, 0);
        const totalAcqOptins = acqValid.reduce((sum, r) => sum + r.acquisition!.totalOptins, 0);
        const avgVisitors = acqValid.length > 0 ? totalAcqVisitors / acqValid.length : null;
        const avgOptinRate = totalAcqVisitors > 0 ? totalAcqOptins / totalAcqVisitors * 100 : null;

        setEocFactor(eocFactor);
        // An historical average must be total recorded cash ÷ webinar count, not a
        // product of independently averaged conversion rates from different cohorts.
        setBenchmarkActualCash(totalCash / valid.length);
        setBenchmarkWebinarCount(valid.length);
        setMetrics((current) => ({
          ...current,
          ...(avgVisitors !== null  ? { visitors: Math.round(avgVisitors) } : {}),
          ...(avgOptinRate !== null ? { optinRate: avgOptinRate }           : {}),
          webinarShowRate,
          pitchRetentionRate,
          fullApplicationRate,
          bookingRate,
          callShowRate,
          closeRate,
          cashPerClose:        cashPerClose || current.cashPerClose,
        }));

        const benchmarkLabel = id === "__average__" ? "all webinars" : `${valid.length} selected webinar${valid.length !== 1 ? "s" : ""}`;
        setLoadedName(`Average of ${benchmarkLabel}`);
        setLoadNote(
          `Combined KPIs across ${benchmarkLabel}, calculated from total funnel numerators ÷ total denominators.${acqValid.length > 0 ? ` Acquisition data is included for ${acqValid.length}.` : " GA4 visitor data unavailable, so acquisition sliders were left unchanged."}`
        );
      } catch (error) {
        setLoadNote(error instanceof Error ? error.message : "Unable to compute average KPIs");
      } finally { setLoadingActuals(false); }
      return;
    }

    // ── Single webinar ───────────────────────────────────────────────────────
    setLoadingActuals(true); setLoadNote(null);
    try {
      const webinar = webinars.find((item) => item.id === id);
      if (!webinar) throw new Error("Selected webinar could not be found");
      const acquisitionParams = new URLSearchParams({ startDate: webinar.windowStart, endDate: webinar.windowEnd });
      const [enhancedResponse, acquisitionResponse] = await Promise.all([
        fetch(`/api/webinar2/enhanced/${encodeURIComponent(id)}`),
        fetch(`/api/webinar-acquisition?${acquisitionParams.toString()}`),
      ]);
      const [payload, acquisitionPayload] = await Promise.all([enhancedResponse.json(), acquisitionResponse.json()]);
      if (!payload.success) throw new Error(payload.error || "Unable to load webinar KPIs");
      if (!acquisitionPayload.success) throw new Error(acquisitionPayload.error || "Unable to load acquisition KPIs");
      setBenchmarkActualCash(null);
      setBenchmarkWebinarCount(0);
      const actual = payload.data as EnhancedData;
      const acquisition = acquisitionPayload.data as AcquisitionData;
      const atPitch  = actual.onWebinar.attendeesAtPitch;
      const fullApps = actual.salesTotal.fullApps;
      // Set eocFactor so the formula bookedCalls × eocFactor × EOC_showRate = takenCalls
      setEocFactor(actual.salesTotal.bookedCalls > 0 ? actual.totalEocsDue / actual.salesTotal.bookedCalls : 1);
      setMetrics((current) => ({
        ...current,
        visitors:            acquisition.trafficAvailable ? acquisition.totalVisitors : current.visitors,
        optinRate:           acquisition.trafficAvailable && acquisition.totalOptinRate !== null ? acquisition.totalOptinRate : current.optinRate,
        webinarShowRate:     actual.onWebinar.showRate || current.webinarShowRate,
        pitchRetentionRate:  actual.onWebinar.retentionAtPitch || current.pitchRetentionRate,
        fullApplicationRate: atPitch > 0 ? fullApps / atPitch * 100 : current.fullApplicationRate,
        // Booking rate = booked calls ÷ attendees at pitch
        bookingRate:         atPitch > 0 ? actual.salesTotal.bookedCalls / atPitch * 100 : current.bookingRate,
        callShowRate:        actual.totalEocsDue > 0 ? (actual.totalEocsTaken / actual.totalEocsDue) * 100 : current.callShowRate,
        closeRate:           actual.salesTotal.closeRate || current.closeRate,
        cashPerClose:        actual.salesTotal.auv || current.cashPerClose,
      }));
      setLoadedName(`${webinar.name} (${webinar.date})`);
      setLoadNote(acquisition.trafficAvailable
        ? `Loaded acquisition actuals for ${acquisition.startDate}–${acquisition.endDate}: ${count(acquisition.totalVisitors)} unique visitors, ${count(acquisition.totalOptins)} opt-ins, and ${acquisition.totalOptinRate?.toFixed(1)}% opt-in rate — plus the measured Webinar KPIs.`
        : "Loaded the measured Webinar KPIs. GA4 visitor data was unavailable, so the acquisition sliders were left unchanged.");
    } catch (error) {
      setLoadNote(error instanceof Error ? error.message : "Unable to load webinar KPIs");
    } finally { setLoadingActuals(false); }
  };

  const displayedCash = benchmarkActualCash ?? projection.cash;
  const displayedCashPerCall = benchmarkActualCash !== null && projection.takenCalls > 0
    ? benchmarkActualCash / projection.takenCalls
    : projection.cashPerCall;

  return (
    <div className="mx-auto max-w-[1600px] space-y-6 pb-10">
      <header className="rounded-2xl border border-border bg-card px-6 py-5">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-[var(--brand-primary)]">Tools · Webinar Projections</p>
        <div className="mt-2 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-foreground">Webinar revenue projector</h1>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">Adjust the drivers on the left. The projected funnel and expected end-of-day cash update immediately on the right.</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Cold / Warm audience toggle */}
            <div className="flex items-center rounded-xl border border-border bg-background p-1 gap-1">
              <button
                onClick={() => switchAudience("cold")}
                className={`rounded-lg px-4 py-1.5 text-sm font-bold transition-all ${
                  audienceType === "cold"
                    ? "bg-[var(--brand-primary)] text-white shadow"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                ❄️ Cold
              </button>
              <button
                onClick={() => switchAudience("warm")}
                className={`rounded-lg px-4 py-1.5 text-sm font-bold transition-all ${
                  audienceType === "warm"
                    ? "bg-[var(--brand-primary)] text-white shadow"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                🔥 Warm
              </button>
            </div>
            <button onClick={() => { setMetrics(DEFAULTS); setLoadedName(null); setLoadNote(null); setEocFactor(1.0); }} className="rounded-lg border border-border px-4 py-2 text-sm font-semibold text-muted-foreground transition-colors hover:border-foreground hover:text-foreground">Reset assumptions</button>
          </div>
        </div>
        {/* Audience context hint */}
        <p className="mt-3 text-xs text-muted-foreground">
          {audienceType === "cold"
            ? "❄️ Cold audience — targets set for new traffic with no prior brand exposure."
            : "🔥 Warm audience — targets set for retargeted or email-list traffic with prior brand exposure."}
        </p>
      </header>

      <div className="grid items-start gap-6 xl:grid-cols-[minmax(360px,0.85fr)_minmax(0,1.35fr)]">
        <aside className="space-y-5 xl:sticky xl:top-6">
          <section className="rounded-2xl border border-border bg-card p-5">
            <h2 className="text-lg font-bold text-foreground">Projection controls</h2>
            <p className="mt-1 text-sm text-muted-foreground">Every field is an editable assumption. Percentages are conversion rates from the prior step. <span className="inline-flex items-center gap-1"><span className="inline-block h-2 w-4 rounded-full bg-yellow-400 align-middle" /> yellow bar = target range.</span></p>
            <div className="mt-5 space-y-5">

              {/* 1 · Acquisition */}
              <div>
                <p className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">1 · Acquisition</p>
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
                  <NumericControl
                    label="Unique visitors"
                    hint="Landing-page visitors expected"
                    value={metrics.visitors}
                    suffix="visitors"
                    max={Math.max(50000, Math.ceil(metrics.visitors / 100) * 100)}
                    step={100}
                    onChange={(v) => update("visitors", v)}
                  />
                  <NumericControl
                    label="Opt-in rate"
                    hint="Visitors who opt into the webinar"
                    value={metrics.optinRate}
                    suffix="%"
                    max={100}
                    step={0.1}
                    target={targets.optinRate}
                    onChange={(v) => update("optinRate", v)}
                  />
                </div>
              </div>

              {/* 2 · Webinar */}
              <div>
                <p className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">2 · Webinar</p>
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
                  <NumericControl
                    label="Webinar show rate"
                    hint="Opt-ins who attend live"
                    value={metrics.webinarShowRate}
                    suffix="%"
                    max={100}
                    step={0.1}
                    target={targets.webinarShowRate}
                    onChange={(v) => update("webinarShowRate", v)}
                  />
                  <NumericControl
                    label="Retention at pitch"
                    hint="Attendees still present at the pitch"
                    value={metrics.pitchRetentionRate}
                    suffix="%"
                    max={100}
                    step={0.1}
                    target={targets.pitchRetentionRate}
                    onChange={(v) => update("pitchRetentionRate", v)}
                  />
                </div>
              </div>

              {/* 3 · Sales */}
              <div>
                <p className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">3 · Sales</p>
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
                  <NumericControl
                    label="Full application rate"
                    hint="App pitch viewers who complete a full app"
                    value={metrics.fullApplicationRate}
                    suffix="%"
                    max={100}
                    step={0.1}
                    target={targets.fullApplicationRate}
                    onChange={(v) => update("fullApplicationRate", v)}
                  />
                  <NumericControl
                    label="Booking rate"
                    hint="App pitch viewers who book a call"
                    value={metrics.bookingRate}
                    suffix="%"
                    max={100}
                    step={0.1}
                    target={targets.bookingRate}
                    onChange={(v) => update("bookingRate", v)}
                  />
                  <NumericControl
                    label="Call show rate"
                    hint="Taken EOC calls ÷ EOC calls due (not booking conversions)"
                    value={metrics.callShowRate}
                    suffix="%"
                    max={100}
                    step={0.1}
                    target={targets.callShowRate}
                    onChange={(v) => update("callShowRate", v)}
                  />
                  <NumericControl
                    label="Close rate"
                    hint="Taken calls that close"
                    value={metrics.closeRate}
                    suffix="%"
                    max={100}
                    step={0.1}
                    target={targets.closeRate}
                    onChange={(v) => update("closeRate", v)}
                  />
                  <NumericControl
                    label="Cash per close"
                    hint="Average cash collected per closed deal"
                    value={metrics.cashPerClose}
                    suffix="$"
                    max={20000}
                    step={100}
                    onChange={(v) => update("cashPerClose", v)}
                  />
                </div>
              </div>

            </div>
          </section>

          <section className="rounded-2xl border border-border bg-card p-5">
            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Use a webinar as your benchmark</p>
            <p className="mt-1 text-sm text-muted-foreground">Pick a benchmark and all sliders update instantly — no button needed.</p>
            <div className="relative mt-4">
              <select
                value={selectedWebinar}
                disabled={loadingActuals}
                onChange={(event) => {
                  const val = event.target.value;
                  setSelectedWebinar(val);
                  setSelectedWebinarIds([]);
                  if (val) loadWebinarActuals(val);
                }}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground outline-none focus:border-[var(--brand-primary)] disabled:opacity-60"
              >
                <option value="">Select a benchmark…</option>
                <option value="__average__">📊 Average — all webinars combined</option>
                <optgroup label="Individual webinars">
                  {webinars.map((webinar) => (
                    <option key={webinar.id} value={webinar.id}>{webinar.date} · {webinar.name}</option>
                  ))}
                </optgroup>
              </select>
              {loadingActuals && (
                <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-[var(--brand-primary)] border-t-transparent" />
                  {selectedWebinar === "__average__" || selectedWebinar === "__selected__" ? "Loading combined webinar KPIs…" : "Loading webinar KPIs…"}
                </div>
              )}
            </div>
            <div className="mt-3 rounded-xl border border-border bg-background/50 p-3">
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs font-semibold text-foreground">Custom combined benchmark</p>
                <span className="text-xs text-muted-foreground">{selectedWebinarIds.length} selected</span>
              </div>
              <div className="mt-2 space-y-1.5">
                {webinars.map((webinar) => {
                  const checked = selectedWebinarIds.includes(webinar.id);
                  return <label key={webinar.id} className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
                    <input type="checkbox" checked={checked} disabled={loadingActuals} onChange={() => setSelectedWebinarIds((current) => checked ? current.filter((id) => id !== webinar.id) : [...current, webinar.id])} />
                    <span>{webinar.date} · {webinar.name}</span>
                  </label>;
                })}
              </div>
              <button
                type="button"
                disabled={loadingActuals || selectedWebinarIds.length === 0}
                onClick={() => {
                  const selected = webinars.filter((webinar) => selectedWebinarIds.includes(webinar.id));
                  setSelectedWebinar("__selected__");
                  loadWebinarActuals("__selected__", selected);
                }}
                className="mt-3 w-full rounded-lg bg-[var(--brand-primary)] px-3 py-2 text-xs font-bold text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                Use selected webinars as benchmark
              </button>
            </div>
            {(loadedName || loadNote) && !loadingActuals && (
              <p className="mt-3 text-xs leading-5 text-muted-foreground">
                {loadedName && <><span className="font-semibold text-foreground">Benchmark: {loadedName}.</span> </>}
                {loadNote}
              </p>
            )}
          </section>
        </aside>

        <main className="space-y-5">
          <section className="overflow-hidden rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/15 via-card to-card p-6">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-400">Projected end-of-day cash</p>
            <div className="mt-2 flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="font-mono text-5xl font-black tracking-tight text-foreground sm:text-6xl">{money(displayedCash)}</p>
                <p className="mt-2 text-sm text-muted-foreground">{benchmarkActualCash !== null ? `Average actual cash across ${benchmarkWebinarCount} webinar${benchmarkWebinarCount === 1 ? "" : "s"}` : `${count(projection.closes)} projected closes × ${money(metrics.cashPerClose)} cash per close`}</p>
              </div>
              <div className="rounded-xl border border-border bg-card/70 px-4 py-3 text-right">
                <p className="text-xs text-muted-foreground">Cash per taken call</p>
                <p className="mt-1 font-mono text-xl font-bold text-foreground">{money(displayedCashPerCall)}</p>
              </div>
            </div>
          </section>

          <section className="rounded-2xl border border-border bg-card p-6">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <h2 className="text-xl font-bold text-foreground">Projected webinar funnel</h2>
                <p className="mt-1 text-sm text-muted-foreground">These formulas use the same KPI definitions displayed in Marketing → Webinars.</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const scenarioLabel = loadedName ? loadedName : "Custom scenario";
                    const audienceLabel = audienceType === "cold" ? "Cold" : "Warm";
                    const perfLabel = (val: number, t?: TargetRange): string => {
                      const s = getPerfStatus(val, t);
                      if (s === "above") return "↑ Exceeding";
                      if (s === "below") return "↓ Below target";
                      if (s === "on") return "✓ On target";
                      return "—";
                    };
                    const targetLabel = (t?: TargetRange): string => t ? `${t.min}%–${t.max}%` : "—";
                    const rows: string[][] = [
                      ["Cr8Her — Webinar Projections Export"],
                      [`Scenario: ${scenarioLabel} · Audience: ${audienceLabel} · Generated: ${new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}`],
                      [],
                      ["── ASSUMPTIONS (rates) ──"],
                      ["Metric", "Value", "Target range", "Performance"],
                      ["Unique visitors", String(metrics.visitors), "—", "—"],
                      ["Opt-in rate", `${metrics.optinRate.toFixed(1)}%`, targetLabel(targets.optinRate), perfLabel(metrics.optinRate, targets.optinRate)],
                      ["Webinar show rate", `${metrics.webinarShowRate.toFixed(1)}%`, targetLabel(targets.webinarShowRate), perfLabel(metrics.webinarShowRate, targets.webinarShowRate)],
                      ["Retention at pitch", `${metrics.pitchRetentionRate.toFixed(1)}%`, targetLabel(targets.pitchRetentionRate), perfLabel(metrics.pitchRetentionRate, targets.pitchRetentionRate)],
                      ["Full application rate", `${metrics.fullApplicationRate.toFixed(1)}%`, targetLabel(targets.fullApplicationRate), perfLabel(metrics.fullApplicationRate, targets.fullApplicationRate)],
                      ["Booking rate (of pitch viewers)", `${metrics.bookingRate.toFixed(1)}%`, targetLabel(targets.bookingRate), perfLabel(metrics.bookingRate, targets.bookingRate)],
                      ["Call show rate", `${metrics.callShowRate.toFixed(1)}%`, targetLabel(targets.callShowRate), perfLabel(metrics.callShowRate, targets.callShowRate)],
                      ["Close rate", `${metrics.closeRate.toFixed(1)}%`, targetLabel(targets.closeRate), perfLabel(metrics.closeRate, targets.closeRate)],
                      ["Cash per close", `$${metrics.cashPerClose.toLocaleString()}`, "—", "—"],
                      [],
                      ["── PROJECTED FUNNEL ──"],
                      ["Stage", "Projected count", "Conversion from prior stage"],
                      ["Unique visitors", String(Math.round(metrics.visitors)), "—"],
                      ["Opt-ins", projection.optins.toFixed(1), `${metrics.optinRate.toFixed(1)}%`],
                      ["Live attendees", projection.attended.toFixed(1), `${metrics.webinarShowRate.toFixed(1)}%`],
                      ["Attendees at pitch", projection.atPitch.toFixed(1), `${metrics.pitchRetentionRate.toFixed(1)}%`],
                      ["Full applications", projection.fullApps.toFixed(1), `${metrics.fullApplicationRate.toFixed(1)}%`],
                      ["Calls booked", projection.bookedCalls.toFixed(1), `${metrics.bookingRate.toFixed(1)}% of pitch viewers`],
                      ["Calls taken", projection.takenCalls.toFixed(1), `${metrics.callShowRate.toFixed(1)}%`],
                      ["Closes", projection.closes.toFixed(1), `${metrics.closeRate.toFixed(1)}%`],
                      [],
                      ["── CASH SUMMARY ──"],
                      ["Metric", "Value"],
                      ["End-of-day cash", `$${Math.round(projection.cash).toLocaleString()}`],
                      ["Cash per close", `$${metrics.cashPerClose.toLocaleString()}`],
                      ["Cash per taken call", `$${Math.round(projection.cashPerCall).toLocaleString()}`],
                    ];
                    const csv = rows.map((r) => r.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(",")).join("\n");
                    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `webinar-projection-${new Date().toISOString().slice(0, 10)}.csv`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  className="flex items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-1.5 text-xs font-semibold text-muted-foreground transition-colors hover:border-[var(--brand-primary)] hover:text-[var(--brand-primary)]"
                >
                  ⬇ Download CSV
                </button>
                <span className="rounded-full bg-muted px-3 py-1 text-xs font-semibold text-muted-foreground">Live scenario</span>
              </div>
            </div>
            <div className="mt-5">
              <ResultRow label="Unique visitors" value={metrics.visitors} />
              <ResultRow label="Opt-ins" value={projection.optins} rate={metrics.optinRate} />
              <ResultRow label="Live attendees" value={projection.attended} rate={metrics.webinarShowRate} />
              <ResultRow label="Attendees at pitch" value={projection.atPitch} rate={metrics.pitchRetentionRate} />
              <ResultRow label="Full applications" value={projection.fullApps} rate={metrics.fullApplicationRate} />
              <ResultRow label="Calls booked" value={projection.bookedCalls} rate={metrics.bookingRate} rateLabel="of app pitch viewers" />
              <ResultRow label="Calls taken" value={projection.takenCalls} rate={metrics.callShowRate} />
              <ResultRow label="Closes" value={projection.closes} rate={metrics.closeRate} />
              <ResultRow label={benchmarkActualCash !== null ? "Average actual cash" : `End-of-day cash · ${money(metrics.cashPerClose)} per close`} value={displayedCash} final />
            </div>
          </section>

          <section className="rounded-2xl border border-border bg-card p-5 text-sm text-muted-foreground">
            <p className="font-semibold text-foreground">Metric definitions</p>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              <li><strong className="text-foreground">Opt-in rate</strong> = webinar opt-ins ÷ unique visitors.</li>
              <li><strong className="text-foreground">Show rate</strong> = live attendees ÷ opt-ins; <strong className="text-foreground">retention at pitch</strong> = attendees still watching at the pitch ÷ live attendees.</li>
              <li><strong className="text-foreground">Full application rate</strong> = full apps ÷ attendees at pitch; <strong className="text-foreground">booking rate</strong> = booked calls ÷ attendees at pitch.</li>
              <li><strong className="text-foreground">Sales-call show rate</strong> = taken EOC calls ÷ EOC calls due. It is intentionally separate from the Calls Booked conversion count.</li>
              <li><strong className="text-foreground">End-of-day cash</strong> = projected closes × projected cash per close. It models collected cash, not contracted revenue.</li>
            </ul>
          </section>
        </main>
      </div>
    </div>
  );
}
